import React, { useState } from "react";
import { SoundToggle } from "@/components/SoundToggle";
import { Wrench, RotateCcw, Check } from "lucide-react";
import { StaffViewer } from "@/components/StaffViewer";
import { playRight, playWrong, playFanfare } from "@/lib/feedbackSounds";

const N = (pitch, duration, label, extra = {}) => ({ pitch, duration, label, ...extra });
const MSG = {
  dot: "Correct! The dot was missing: a dotted crotchet lasts 1½ beats.",
  bar: "Correct! Bar 1 ends after the quaver: 1½ + ½ = 2 beats.",
  last: "Correct! A minim made the last bar 3 beats. A crotchet makes it triplet (1) + 1 = 2.",
};

const build = (f, id) => [
  N("G4", "quarter", "1½", { dot: f.dot, fix: f.dot ? null : "dot", testId: `${id}-note-first` }),
  N("A4", "eighth", "½"),
  ...(f.bar ? [{ barline: true }] : []),
  N("B4", "16th", "¼"), N("C5", "16th", "¼"), N("B4", "16th", "¼"), N("A4", "16th", "¼"),
  N("G4", "quarter", "1"),
  { barline: true },
  N("A4", "eighth", "⅓", { tuplet: 3 }), N("B4", "eighth", "⅓", { tuplet: 3 }), N("C5", "eighth", "⅓", { tuplet: 3 }),
  f.last ? N("B4", "quarter", "1", { testId: `${id}-note-last` }) : N("B4", "half", "2", { fix: "last", testId: `${id}-note-last` }),
  { barline: "double" },
];

// Fix-the-mistakes: a 2/4 stave with a missing dot, a missing barline and a bar that doesn't add up.
export const FixRhythm = ({ testId = "fix-rhythm" }) => {
  const [f, setF] = useState({ dot: false, bar: false, last: false });
  const [msg, setMsg] = useState(null);
  const done = Object.values(f).filter(Boolean).length;
  const [slips, setSlips] = useState(0);
  const fix = (k) => {
    setF({ ...f, [k]: true }); setMsg({ ok: true, text: MSG[k] }); playRight();
    if (done === 2 && !slips) playFanfare(0.35, "Fix the mistakes");
  };
  const miss = (text) => { setMsg({ ok: false, text }); setSlips(slips + 1); playWrong(); };
  const onNote = (n) => (n.fix ? fix(n.fix) : miss("That note is fine. Count the beats in each bar and look again."));
  const onGap = (k) => (!f.bar && k === 1 ? fix("bar") : miss("No barline is missing there. Count 2 beats from the start of the bar."));

  return (
    <div className="mt-4 rounded-2xl border border-primary/30 bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Wrench size={14} /> Fix the mistakes</p>
        <SoundToggle testId={`${testId}-sound-toggle`} className="ml-auto" />
        <p className="text-xs font-semibold" data-testid={`${testId}-count`}>Mistakes fixed: {done} / 3</p>
      </div>
      <p className="mt-2 text-sm">There are 3 mistakes: a missing dot, a missing barline and a bar that doesn't add up to 2 beats. Tap a wrong note to correct it, or tap the space where the missing barline should go.</p>
      <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-background/60 p-2">
        <div className="min-w-[520px]">
          <StaffViewer clef="treble" time="2/4" notes={build(f, testId)} width={600} showLabels onNoteClick={onNote} onGapClick={f.bar ? undefined : onGap} testId={`${testId}-staff`} />
        </div>
      </div>
      {msg && (
        <p className={`mt-3 rounded-xl px-3 py-2 text-sm font-semibold ${msg.ok ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"}`} data-testid={`${testId}-feedback`}>{msg.text}</p>
      )}
      {done === 3 && (
        <div className="mt-3 flex flex-wrap items-center gap-3" data-testid={`${testId}-done`}>
          <p className="flex items-center gap-1.5 text-sm font-bold"><Check size={15} className="text-emerald-500" /> All fixed! Every bar now adds up to 2 beats.</p>
          <button type="button" onClick={() => { setF({ dot: false, bar: false, last: false }); setMsg(null); setSlips(0); }} data-testid={`${testId}-restart`}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:border-primary"><RotateCcw size={13} /> Try again</button>
        </div>
      )}
    </div>
  );
};
